import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  TIMEFRAME_OPTIONS,
  CUSTOM_TIMEFRAME_ID,
  CUSTOM_TIMEFRAME_UNITS,
} from "./ohlcAggregation";
import {
  DATE_RANGE_PRESETS,
  CHART_GRID_SIZE_OPTIONS,
  getChartGridOptionLabel,
  formatPrice,
} from "./Chart";
import { groupSymbols } from "./symbolCategories";

/** TradingView-style phone chrome: a compact header + slim bottom bar, with every other
 * control tucked behind one of four bottom sheets. Only mounted when useIsMobile() is true;
 * Chart.js keeps the desktop floating-toolbar system completely separate and unchanged. */
export default function MobileChartChrome({
  palette,
  sheet,
  onOpenSheet,
  onCloseSheet,
  header,
  symbol,
  timeframe,
  drawing,
  more,
  space = false,
}) {
  // Escape closes an open sheet
  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCloseSheet();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet, onCloseSheet]);

  // Opening a panel or replay from a sheet closes the sheet, so the two never stack up.
  const closeAfter = (fn) => (...args) => {
    fn?.(...args);
    onCloseSheet();
  };
  const moreActions = {
    ...more,
    togglePanel: closeAfter(more.togglePanel),
    enterReplaySelection: closeAfter(more.enterReplaySelection),
    takeChartSnapshot: closeAfter(more.takeChartSnapshot),
  };
  const drawingActions = {
    ...drawing,
    toggleDrawTool: closeAfter(drawing.toggleDrawTool),
    setIsMeasureMode: closeAfter(drawing.setIsMeasureMode),
    clearDrawings: closeAfter(drawing.clearDrawings),
  };

  const barStyle = {
    position: "absolute",
    zIndex: 5,
    display: "flex",
    alignItems: "center",
    borderRadius: space ? "16px" : "10px",
    border: `1px solid ${palette.borderSoft}`,
    background: palette.toolbarBackground,
    backdropFilter: "blur(18px)",
    boxShadow: space ? "none" : "0 6px 16px rgba(0, 0, 0, 0.28)",
    color: palette.shellText,
    ...(space ? { fontFamily: SPACE_FONT } : null),
  };

  const legendCandle =
    header.hoveredCandle ||
    (header.visibleData && header.visibleData.length
      ? header.visibleData[header.visibleData.length - 1]
      : null);
  const prevCandle =
    legendCandle && header.visibleData
      ? header.visibleData[
          header.visibleData.findIndex((c) => c.time === legendCandle.time) - 1
        ]
      : null;
  const change = legendCandle && prevCandle ? legendCandle.close - prevCandle.close : 0;
  const changePct = prevCandle && prevCandle.close ? (change / prevCandle.close) * 100 : 0;
  const isUp = change >= 0;
  const changeColor = isUp ? palette.stUp || "#22c55e" : palette.stDown || "#ef4444";
  const currentTfLabel =
    TIMEFRAME_OPTIONS.find((o) => o.id === timeframe.chartTimeframeId)?.label ||
    timeframe.chartTimeframeId;
  const symbolLabel = symbol.liveRatioSymbols
    ? `${symbol.liveRatioSymbols.leg1}/${symbol.liveRatioSymbols.leg2}`
    : symbol.marketSymbol || "Symbol";

  const badgeChar = (symbolLabel || "?").trim().charAt(0).toUpperCase();

  return (
    <>
      {/* Compact top header, TradingView-style: badge + symbol on row 1, big price on row 2, change on row 3 */}
      <button
        type="button"
        onClick={() => onOpenSheet("symbol")}
        style={{
          ...barStyle,
          ...(space
            ? { borderRadius: 16, border: `1px solid ${palette.borderSoft}`, background: palette.overlayBackground }
            : null),
          top: "14px",
          left: "14px",
          right: "14px",
          minHeight: "92px",
          padding: "10px 12px",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 4,
          textAlign: "left",
          cursor: "pointer",
        }}
        aria-label="Open symbol switcher"
      >
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span
              style={{
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: palette.buttonActiveBackground,
                border: `1px solid ${palette.activeBorder}`,
                color: palette.shellText,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.62rem",
                fontWeight: 800,
              }}
            >
              {badgeChar}
            </span>
            <span
              style={{
                fontSize: "0.88rem",
                fontWeight: 800,
                letterSpacing: "0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {symbolLabel}
            </span>
            <span style={{ fontSize: "0.66rem", fontWeight: 700, color: palette.mutedText }}>
              {currentTfLabel}
            </span>
          </span>
          <span
            style={{
              flexShrink: 0,
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: `1px solid ${palette.borderSoft}`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.6rem",
              color: palette.mutedText,
            }}
          >
            ▾
          </span>
        </span>
        {legendCandle ? (
          <>
            <span style={{ display: "flex", alignItems: "baseline", gap: 8, paddingLeft: 30 }}>
              <span style={{ fontSize: "1.32rem", fontWeight: space ? 600 : 800, lineHeight: 1, ...(space ? { fontFamily: SPACE_FONT, fontWeight: 500, letterSpacing: "-0.03em" } : null) }}>
                {formatPrice(legendCandle.close)}
              </span>
              <span style={{ fontSize: "0.76rem", fontWeight: 700, color: changeColor }}>
                {isUp ? "+" : ""}
                {formatPrice(change)} ({isUp ? "+" : ""}
                {changePct.toFixed(2)}%)
              </span>
            </span>
            <span style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px", paddingLeft: 30 }}>
              <OhlcField label="O" value={legendCandle.open} palette={palette} />
              <OhlcField label="H" value={legendCandle.high} palette={palette} />
              <OhlcField label="L" value={legendCandle.low} palette={palette} />
              <OhlcField label="C" value={legendCandle.close} palette={palette} />
            </span>
          </>
        ) : null}
      </button>

      {/* Slim bottom bar, GoCharting-style: labeled icon+text tabs */}
      <nav
        aria-label="Chart tools"
        style={{
          ...barStyle,
          left: "14px",
          right: "14px",
          bottom: "14px",
          minHeight: "56px",
          padding: "4px 2px",
          gap: 2,
          justifyContent: "space-between",
          ...(space
            ? {
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 0,
              border: "none",
              borderTop: `1px solid ${palette.borderSoft}`,
              minHeight: "64px",
              padding: "4px 4px calc(4px + env(safe-area-inset-bottom))",
            }
            : null),
        }}
      >
        <LabeledBarButton
          space={space}
          icon={<ClockIcon />}
          label="Time"
          onClick={() => onOpenSheet("timeframe")}
          palette={palette}
          active={sheet === "timeframe"}
        />
        <LabeledBarButton
          space={space}
          icon={<PencilIcon />}
          label="Draw"
          onClick={() => onOpenSheet("tools")}
          palette={palette}
          active={drawing.drawTool !== "none" || sheet === "tools"}
        />
        <LabeledBarButton
          space={space}
          icon={<span style={{ fontStyle: "italic", fontWeight: 700, fontFamily: SPACE_FONT }}>fx</span>}
          label="Indicators"
          onClick={closeAfter(more.onOpenIndicators)}
          palette={palette}
          active={more.activePanel === "indicators"}
        />
        <LabeledBarButton
          space={space}
          icon={<DotsIcon />}
          label="More"
          onClick={() => onOpenSheet("more")}
          palette={palette}
          active={sheet === "more"}
        />
        <Link
          to="/python"
          title="Python & Backtest"
          style={{ ...barButtonStyle(palette, false, space), textDecoration: "none" }}
        >
          <CodeIcon />
          <span style={barLabelStyle(space)}>Python</span>
        </Link>
      </nav>

      {sheet ? (
        <div
          aria-hidden="true"
          onClick={onCloseSheet}
          style={{ position: "absolute", inset: 0, zIndex: 6, background: "rgba(3, 6, 12, 0.45)" }}
        />
      ) : null}
      {sheet ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={
            sheet === "symbol" ? "Symbol and chart" : sheet === "timeframe" ? "Timeframe and range" : sheet === "tools" ? "Drawing tools" : "More"
          }
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 7,
            maxHeight: "70vh",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: "14px 14px calc(14px + env(safe-area-inset-bottom))",
            borderRadius: space ? "12px 12px 0 0" : "16px 16px 0 0",
            ...(space ? { fontFamily: SPACE_FONT } : null),
            border: `1px solid ${palette.borderSoft}`,
            background: palette.overlayBackground,
            backdropFilter: "blur(18px)",
            boxShadow: "0 -12px 32px rgba(0,0,0,0.35)",
            color: palette.shellText,
          }}
        >
          <SheetHeader
            title={
              sheet === "symbol"
                ? "Symbol & Chart"
                : sheet === "timeframe"
                  ? "Timeframe & Range"
                  : sheet === "tools"
                    ? "Drawing Tools"
                    : "More"
            }
            onClose={onCloseSheet}
            palette={palette}
          />
          {sheet === "symbol" ? <SymbolSheet symbol={symbol} palette={palette} /> : null}
          {sheet === "timeframe" ? <TimeframeSheet timeframe={timeframe} palette={palette} /> : null}
          {sheet === "tools" ? <ToolsSheet drawing={drawingActions} palette={palette} /> : null}
          {sheet === "more" ? <MoreSheet more={moreActions} palette={palette} /> : null}
        </div>
      ) : null}
    </>
  );
}

function OhlcField({ label, value, palette }) {
  return (
    <span style={{ fontSize: "0.68rem", fontWeight: 600, color: palette.mutedText, whiteSpace: "nowrap" }}>
      {label} <span style={{ color: palette.shellText, fontWeight: 700 }}>{formatPrice(value)}</span>
    </span>
  );
}

const SPACE_FONT = '"Geist", "Archivo", system-ui, sans-serif';

function barButtonStyle(palette, active, space) {
  return {
    appearance: "none",
    border: `1px solid ${active ? palette.activeBorder : "transparent"}`,
    background: active ? palette.buttonActiveBackground : "transparent",
    color: active ? palette.shellText : space ? palette.shellText : palette.mutedText,
    borderRadius: space ? 4 : 8,
    flex: 1,
    minWidth: 0,
    minHeight: 48,
    padding: "4px 2px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    cursor: "pointer",
    font: "inherit",
  };
}

function barLabelStyle(space) {
  return space
    ? { fontSize: "0.66rem", fontWeight: 500, letterSpacing: "0.03em", fontFamily: SPACE_FONT }
    : { fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.02em" };
}

function LabeledBarButton({ icon, label, onClick, palette, active, space = false }) {
  return (
    <button type="button" onClick={onClick} title={label} aria-pressed={Boolean(active)} style={barButtonStyle(palette, active, space)}>
      <span aria-hidden="true" style={{ fontSize: "0.95rem", lineHeight: 1, fontWeight: 700, display: "inline-flex" }}>{icon}</span>
      <span style={barLabelStyle(space)}>{label}</span>
    </button>
  );
}

const iconProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, "aria-hidden": true };
function ClockIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 20l4-1 11-11-3-3L5 16l-1 4z" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="5" cy="12" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="19" cy="12" r="1.3" fill="currentColor" />
    </svg>
  );
}
function CodeIcon() {
  return (
    <svg {...iconProps}>
      <path d="M8 7l-5 5 5 5M16 7l5 5-5 5" />
    </svg>
  );
}

function SheetHeader({ title, onClose, palette }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ fontSize: "0.86rem", fontWeight: 800, letterSpacing: "0.03em" }}>{title}</div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          color: palette.mutedText,
          cursor: "pointer",
          fontSize: "1.3rem",
          lineHeight: 1,
          width: 40,
          height: 40,
        }}
      >
        ×
      </button>
    </div>
  );
}

function rowButtonStyle(palette, isActive) {
  return {
    appearance: "none",
    textAlign: "left",
    cursor: "pointer",
    width: "100%",
    padding: "10px 12px",
    minHeight: 44,
    borderRadius: 6,
    border: `1px solid ${isActive ? palette.activeBorder : palette.borderSoft}`,
    background: isActive ? palette.buttonActiveBackground : "transparent",
    color: palette.shellText,
    fontSize: "0.82rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  };
}

function sectionLabelStyle(palette) {
  return {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    color: palette.mutedText,
    margin: "4px 0 -4px",
  };
}

function SymbolSheet({ symbol, palette }) {
  const groups = useMemo(() => groupSymbols(symbol.marketSymbols, symbol.symbolSearchText), [symbol.symbolSearchText, symbol.marketSymbols]);

  const isRatioQuery = symbol.symbolSearchQuery?.mode === "ratio";
  const ratioReady = isRatioQuery && symbol.symbolSearchQuery.leg1 && symbol.symbolSearchQuery.leg2;

  return (
    <>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="text"
          inputMode="text"
          enterKeyHint="go"
          value={symbol.symbolSearchText}
          onChange={(e) => symbol.setSymbolSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") symbol.commitSymbolSelection(symbol.symbolSearchText);
          }}
          placeholder="Symbol or SYM1/SYM2 for a ratio"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "10px 12px",
            borderRadius: 10,
            border: `1px solid ${palette.borderStrong}`,
            background: palette.chipBackground,
            color: palette.shellText,
            fontSize: "0.84rem",
            fontWeight: 600,
            outline: "none",
          }}
          aria-label="Symbol search"
        />
        <button
          type="button"
          onClick={() => symbol.commitSymbolSelection(symbol.symbolSearchText)}
          style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "0 0 auto", justifyContent: "center", padding: "0 16px" }}
        >
          Go
        </button>
      </div>
      {isRatioQuery ? (
        ratioReady ? (
          <button
            type="button"
            onClick={() => symbol.commitSymbolSelection(symbol.symbolSearchText)}
            style={rowButtonStyle(palette, false)}
          >
            Show live ratio: {symbol.symbolSearchQuery.leg1} ÷ {symbol.symbolSearchQuery.leg2}
          </button>
        ) : (
          <div style={{ fontSize: "0.76rem", color: palette.mutedText, padding: "0 2px" }}>
            Type SYM1/SYM2, e.g. EURUSD/GBPUSD, then tap Go
          </div>
        )
      ) : null}
      {symbol.liveRatioSymbols ? (
        <button type="button" onClick={symbol.clearRatioMode} style={rowButtonStyle(palette, true)}>
          <span>Live ratio: {symbol.liveRatioSymbols.leg1} ÷ {symbol.liveRatioSymbols.leg2}</span>
          <span>✕</span>
        </button>
      ) : null}
      {!isRatioQuery ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
          {groups.map((group) => (
            <details key={`mobile-group-${group.category}`} open={Boolean(symbol.symbolSearchQuery?.query) || group.category === "Forex" || group.category === "Crypto"}>
              <summary style={{ ...sectionLabelStyle(palette), cursor: "pointer", display: "flex", justifyContent: "space-between", padding: "5px 2px" }}>
                <span>{group.category}</span><span>{group.count}</span>
              </summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingBottom: 5 }}>
                {group.symbols.map((s) => (
                  <button key={`mobile-sym-${s}`} type="button" onClick={() => symbol.commitSymbolSelection(s)} style={rowButtonStyle(palette, s === symbol.marketSymbol && !symbol.liveRatioSymbols)}>{s}</button>
                ))}
              </div>
            </details>
          ))}
          {!groups.length && <div style={{ fontSize: "0.76rem", color: palette.mutedText, padding: 6 }}>No matching tradeable instruments</div>}
        </div>
      ) : null}

      <div style={sectionLabelStyle(palette)}>Panes</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {CHART_GRID_SIZE_OPTIONS.map((n) => (
          <button
            key={`mobile-grid-${n}`}
            type="button"
            onClick={() => symbol.setChartBoxCount(n)}
            style={{ ...rowButtonStyle(palette, symbol.chartBoxCount === n), width: "auto", flex: "1 1 70px", justifyContent: "center" }}
          >
            {getChartGridOptionLabel(n)}
          </button>
        ))}
      </div>

      <div style={sectionLabelStyle(palette)}>Data</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <label style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "1 1 auto", justifyContent: "center", position: "relative" }}>
          Import file
          <input type="file" accept=".xlsx, .csv" onChange={symbol.handleFileUpload} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
        </label>
        <label style={{ ...rowButtonStyle(palette, Boolean(symbol.ratioCandles1.length)), width: "auto", flex: "1 1 auto", justifyContent: "center", position: "relative" }}>
          Ratio dataset 1
          <input type="file" accept=".xlsx, .csv" onChange={symbol.handleRatioFileUpload(1)} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
        </label>
        <label style={{ ...rowButtonStyle(palette, Boolean(symbol.ratioCandles2.length)), width: "auto", flex: "1 1 auto", justifyContent: "center", position: "relative" }}>
          Ratio dataset 2
          <input type="file" accept=".xlsx, .csv" onChange={symbol.handleRatioFileUpload(2)} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
        </label>
        {symbol.ratioCandles1.length && symbol.ratioCandles2.length ? (
          <button
            type="button"
            onClick={() => symbol.setRatioModeEnabled((v) => !v)}
            style={{ ...rowButtonStyle(palette, symbol.ratioModeEnabled), width: "auto", flex: "1 1 auto", justifyContent: "center" }}
          >
            Show ratio 1÷2
          </button>
        ) : null}
      </div>
      {!symbol.isBackendFeedActive ? (
        <button type="button" onClick={() => symbol.setIsBackendFeedActive(true)} style={rowButtonStyle(palette, false)}>
          Resume live feed
        </button>
      ) : null}
    </>
  );
}

function TimeframeSheet({ timeframe, palette }) {
  return (
    <>
      <div style={sectionLabelStyle(palette)}>Timeframe</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {TIMEFRAME_OPTIONS.map((tf) => (
          <button
            key={`mobile-tf-${tf.id}`}
            type="button"
            onClick={() => timeframe.setChartTimeframeId(tf.id)}
            style={{ ...rowButtonStyle(palette, timeframe.chartTimeframeId === tf.id), width: "auto", flex: "0 0 auto", justifyContent: "center", padding: "8px 12px" }}
          >
            {tf.label}
          </button>
        ))}
      </div>
      {timeframe.chartTimeframeId === CUSTOM_TIMEFRAME_ID ? (
        <div style={{ display: "flex", gap: 6 }}>
          <input
            type="text"
            inputMode="decimal"
            value={timeframe.customTimeframeInput}
            onChange={(e) => timeframe.setCustomTimeframeInput(e.target.value)}
            placeholder="15"
            style={{ width: 70, padding: "8px 10px", borderRadius: 8, border: `1px solid ${palette.borderStrong}`, background: palette.chipBackground, color: palette.shellText, fontSize: "0.8rem", fontWeight: 600 }}
            aria-label="Custom timeframe value"
          />
          <select
            value={timeframe.customTimeframeUnit}
            onChange={(e) => timeframe.setCustomTimeframeUnit(e.target.value)}
            style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${palette.borderStrong}`, background: palette.chipBackground, color: palette.shellText, fontSize: "0.8rem", fontWeight: 600 }}
            aria-label="Custom timeframe unit"
          >
            {CUSTOM_TIMEFRAME_UNITS.map((u) => (
              <option key={u.id} value={u.id}>{u.label}</option>
            ))}
          </select>
        </div>
      ) : null}

      <div style={sectionLabelStyle(palette)}>Date range</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {DATE_RANGE_PRESETS.map((preset) => (
          <button
            key={`mobile-range-${preset.key}`}
            type="button"
            onClick={() => timeframe.applyDateRangePreset(preset)}
            style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "0 0 auto", justifyContent: "center", padding: "8px 12px" }}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div style={sectionLabelStyle(palette)}>Go to date</div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          ref={timeframe.goToDateInputRef}
          type="date"
          value={timeframe.goToDate}
          onChange={(e) => {
            timeframe.setGoToDate(e.target.value);
            timeframe.goToSelectedDate(e.target.value);
          }}
          style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${palette.borderStrong}`, background: palette.chipBackground, color: palette.shellText, fontSize: "0.8rem", fontWeight: 600, colorScheme: timeframe.isWhiteTheme ? "light" : "dark" }}
          aria-label="Go to date"
        />
        <button type="button" onClick={() => timeframe.goToSelectedDate()} style={{ ...rowButtonStyle(palette, false), width: "auto", justifyContent: "center", padding: "8px 14px" }}>
          GO
        </button>
      </div>

      <div style={sectionLabelStyle(palette)}>Timezone</div>
      <select
        value={timeframe.chartTimeZone}
        onChange={(e) => timeframe.setChartTimeZone(e.target.value)}
        style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${palette.borderStrong}`, background: palette.chipBackground, color: palette.shellText, fontSize: "0.8rem", fontWeight: 600 }}
        aria-label="Chart timezone"
      >
        {timeframe.chartTimeZoneOptions.map((tz) => (
          <option key={`mobile-tz-${tz.value}`} value={tz.value}>{tz.label}</option>
        ))}
      </select>
    </>
  );
}

function ToolsSheet({ drawing, palette }) {
  const options = drawing.options || [];
  return (
    <>
      {/* tools as an even 3-column grid of big tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))", gap: 8 }}>
        {drawing.toolDefinitions.map((tool) => {
          const Icon = tool.Icon;
          const isActive = drawing.drawTool === tool.key;
          return (
            <button
              key={`mobile-tool-${tool.key}`}
              type="button"
              onClick={() => drawing.toggleDrawTool(tool.key)}
              title={tool.label}
              aria-label={tool.label}
              aria-pressed={isActive}
              style={{
                ...rowButtonStyle(palette, isActive),
                width: "100%",
                minHeight: 72,
                flexDirection: "column",
                justifyContent: "center",
                gap: 6,
                padding: "8px 4px",
                fontSize: "0.72rem",
                lineHeight: 1.2,
                overflowWrap: "anywhere",
                textAlign: "center",
              }}
            >
              {Icon ? <Icon size={20} /> : tool.symbol}
              <span>{tool.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => drawing.setIsMeasureMode((v) => !v)}
          aria-pressed={drawing.isMeasureMode}
          style={{ ...rowButtonStyle(palette, drawing.isMeasureMode), width: "100%", minHeight: 64, flexDirection: "column", justifyContent: "center", gap: 6, padding: "8px 4px", fontSize: "0.72rem" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <path d="M3 17l14-14 4 4L7 21z" />
            <path d="M7 13l2 2M10 10l2 2M13 7l2 2" />
          </svg>
          <span>Measure</span>
        </button>
      </div>
      {options.length ? (
        <>
          <div style={sectionLabelStyle(palette)}>Drawing options</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`, gap: 6 }}>
            {options.map((o) => (
              <button
                key={`mobile-draw-opt-${o.key}`}
                type="button"
                onClick={o.onClick}
                disabled={o.disabled}
                title={o.title}
                aria-label={o.label}
                aria-pressed={o.pressed}
                style={{
                  ...rowButtonStyle(palette, Boolean(o.pressed)),
                  width: "100%",
                  minHeight: 56,
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 4,
                  padding: "6px 2px",
                  fontSize: "0.68rem",
                  opacity: o.disabled ? 0.4 : 1,
                }}
              >
                <o.Icon size={18} off={o.off} />
                <span>
                  {o.key === "magnet"
                    ? `Magnet${o.badge ? ` ${o.badge === "W" ? "weak" : "strong"}` : ""}`
                    : o.key === "stay"
                      ? "Keep tool"
                      : o.key === "hide"
                        ? (o.off ? "Show" : "Hide")
                        : o.label}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : null}
      <button type="button" onClick={drawing.clearDrawings} style={{ ...rowButtonStyle(palette, false), width: "100%", justifyContent: "center", color: palette.dangerText || palette.shellText }}>
        Clear all drawings
      </button>
    </>
  );
}

function MoreSheet({ more, palette }) {
  const lastCandle =
    more.visibleData && more.visibleData.length ? more.visibleData[more.visibleData.length - 1] : null;
  const currentTfLabel =
    TIMEFRAME_OPTIONS.find((o) => o.id === more.chartTimeframeId)?.label || more.chartTimeframeId;

  return (
    <>
      <div style={sectionLabelStyle(palette)}>Panels</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <button type="button" onClick={() => more.togglePanel("settings")} style={{ ...rowButtonStyle(palette, more.activePanel === "settings"), width: "auto", flex: "1 1 100px", justifyContent: "center" }}>Settings</button>
        <button type="button" onClick={() => more.togglePanel("script")} style={{ ...rowButtonStyle(palette, more.activePanel === "script"), width: "auto", flex: "1 1 100px", justifyContent: "center" }}>Script</button>
        <button type="button" onClick={() => more.togglePanel("ai")} style={{ ...rowButtonStyle(palette, more.activePanel === "ai"), width: "auto", flex: "1 1 100px", justifyContent: "center" }}>AI assistant</button>
        <button type="button" onClick={more.enterReplaySelection} style={{ ...rowButtonStyle(palette, more.isReplayMode || more.isReplaySelecting), width: "auto", flex: "1 1 100px", justifyContent: "center" }}>Replay</button>
      </div>

      <div style={sectionLabelStyle(palette)}>Chart view</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <button type="button" onClick={more.takeChartSnapshot} style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "1 1 90px", justifyContent: "center" }}>Snapshot</button>
        <button type="button" onClick={more.toggleChartFullscreen} style={{ ...rowButtonStyle(palette, more.isChartFullscreen), width: "auto", flex: "1 1 90px", justifyContent: "center" }}>{more.isChartFullscreen ? "Exit FS" : "Fullscreen"}</button>
        <button type="button" onClick={() => more.zoomChart("in")} style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "1 1 60px", justifyContent: "center" }}>Zoom +</button>
        <button type="button" onClick={() => more.zoomChart("out")} style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "1 1 60px", justifyContent: "center" }}>Zoom −</button>
        <button type="button" onClick={() => more.panChart("left")} style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "1 1 60px", justifyContent: "center" }}>Pan ←</button>
        <button type="button" onClick={() => more.panChart("right")} style={{ ...rowButtonStyle(palette, false), width: "auto", flex: "1 1 60px", justifyContent: "center" }}>Pan →</button>
      </div>

      <div style={sectionLabelStyle(palette)}>Watchlist / Details / Help</div>
      <div style={{ display: "flex", gap: 6 }}>
        {[
          { key: "watchlist", label: "Watchlist" },
          { key: "details", label: "Details" },
          { key: "info", label: "Help" },
        ].map((tab) => (
          <button
            key={`mobile-rt-${tab.key}`}
            type="button"
            onClick={() => more.setRightSideTab((cur) => (cur === tab.key ? null : tab.key))}
            style={{ ...rowButtonStyle(palette, more.rightSideTab === tab.key), width: "auto", flex: 1, justifyContent: "center" }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {more.rightSideTab === "watchlist" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              more.addToWatchlist(more.watchlistAddText);
              more.setWatchlistAddText("");
            }}
            style={{ display: "flex", gap: 6 }}
          >
            <input
              type="text"
              value={more.watchlistAddText}
              onChange={(e) => more.setWatchlistAddText(e.target.value)}
              placeholder="Add symbol (e.g. USDJPY)"
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${palette.borderStrong}`, background: palette.chipBackground, color: palette.shellText, fontSize: "0.8rem" }}
            />
            <button type="submit" style={{ ...rowButtonStyle(palette, false), width: "auto", justifyContent: "center" }}>Add</button>
          </form>
          {more.watchlistSymbols && more.watchlistSymbols.length ? (
            more.watchlistSymbols.map((sym) => {
              const quote = more.watchlistQuotes[sym];
              const isUp = quote && quote.changeAbs >= 0;
              return (
                <div key={`mobile-wl-${sym}`} style={{ display: "flex", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => more.commitSymbolSelection(sym)}
                    style={{ ...rowButtonStyle(palette, sym === more.marketSymbol), flex: 1 }}
                  >
                    <span>{sym}</span>
                    <span style={{ color: !quote ? palette.mutedText : isUp ? palette.plusDI : palette.minusDI }}>
                      {quote ? `${formatPrice(quote.last)} (${isUp ? "+" : ""}${quote.changePct.toFixed(2)}%)` : "—"}
                    </span>
                  </button>
                  <button type="button" onClick={() => more.removeFromWatchlist(sym)} aria-label={`Remove ${sym}`} style={{ appearance: "none", border: "none", background: "transparent", color: palette.mutedText, cursor: "pointer" }}>×</button>
                </div>
              );
            })
          ) : (
            <div style={{ color: palette.mutedText, fontSize: "0.8rem" }}>No symbols in watchlist yet.</div>
          )}
        </div>
      ) : null}

      {more.rightSideTab === "details" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <DetailRow k="Symbol" v={more.marketSymbol || "—"} palette={palette} />
          <DetailRow k="Timeframe" v={currentTfLabel} palette={palette} />
          <DetailRow k="Bars" v={more.visibleData ? more.visibleData.length : 0} palette={palette} />
          {lastCandle ? (
            <>
              <DetailRow k="Last" v={formatPrice(lastCandle.close)} palette={palette} />
              <DetailRow k="Open" v={formatPrice(lastCandle.open)} palette={palette} />
              <DetailRow k="High" v={formatPrice(lastCandle.high)} palette={palette} />
              <DetailRow k="Low" v={formatPrice(lastCandle.low)} palette={palette} />
            </>
          ) : null}
          <DetailRow k="Active indicators" v={more.activeIndicators.length} palette={palette} />
          <DetailRow k="Live feed" v={more.isBackendFeedActive ? "on" : "off"} palette={palette} />
        </div>
      ) : null}

      {more.rightSideTab === "info" ? (
        <div style={{ fontSize: "0.8rem", lineHeight: 1.55, color: palette.shellText }}>
          <ul style={{ paddingLeft: 16, margin: 0, display: "grid", gap: 4, color: palette.mutedText }}>
            <li>Tap the header to search or switch symbols.</li>
            <li>Tap ƒx to browse indicators.</li>
            <li>The pencil opens drawing tools.</li>
            <li>Tap the symbol · timeframe chip for interval and date range.</li>
          </ul>
        </div>
      ) : null}
    </>
  );
}

function DetailRow({ k, v, palette }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, fontSize: "0.8rem" }}>
      <span style={{ color: palette.mutedText, fontWeight: 600 }}>{k}</span>
      <span style={{ color: palette.shellText, fontWeight: 700 }}>{v}</span>
    </div>
  );
}
